import {
  opticalSaleResponseSchema,
  type OpticalQuote,
  type OpticalSale,
  type SessionIdentity,
  type StandardPaymentMethod,
} from "@cosmo/contracts";
import { useMemo, useState } from "react";
import { emptyFiscalDraft, FiscalRequestFields, invoicePayload, type FiscalDraft } from "./FiscalRequestFields";
import { openAuditedCashDrawer } from "./cash-drawer";
import { downloadPdf, printReceiptPdf } from "./local-print";
import {
  buildCustomerFrameResponsivaPdf,
  buildOpticalCustomerTicketPdf,
  buildOpticalInternalTicketPdf,
  buildOpticalLaboratoryTicketPdf,
  buildOpticalTicketPackagePdf,
  opticalDocumentFilename,
} from "./ticket-pdf";

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" });
const paymentMethods: Array<{ code: StandardPaymentMethod; label: string }> = [
  { code: "01", label: "Efectivo" },
  { code: "03", label: "Transferencia electrónica de fondos" },
  { code: "04", label: "Tarjeta de crédito" },
  { code: "28", label: "Tarjeta de débito" },
];

type PaymentMode = StandardPaymentMethod | "INTERNAL_CREDIT_NOTE";
type PaymentDraft =
  | { kind: "STANDARD"; methodCode: StandardPaymentMethod; reference: string; amount: number }
  | { kind: "INTERNAL_CREDIT_NOTE"; redemptionCode: string; amount: number };

function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

async function responseMessage(response: Response): Promise<string> {
  try {
    const body = await response.json() as { error?: { message?: string; requestId?: string } };
    const message = body.error?.message || "No fue posible completar la operación.";
    return body.error?.requestId ? `${message} Referencia: ${body.error.requestId}.` : message;
  } catch {
    return "No fue posible completar la operación.";
  }
}

function shortDate(value: string): string {
  return new Intl.DateTimeFormat("es-MX", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
}

function eyeLine(label: string, eye: OpticalSale["prescription"]["od"]): string {
  return `${label}: ESF ${eye.sphere.toFixed(2)} CIL ${eye.cylinder.toFixed(2)} EJE ${eye.axis ?? "N/A"} ADD ${eye.addition?.toFixed(2) ?? "N/A"} DNP ${eye.dnp} ALT ${eye.height} COR ${eye.corridor ?? "N/A"}`;
}

function frameMeasurementsLine(frame: OpticalSale["frame"]): string | null {
  const measurements = frame.measurements;
  if (!measurements) return null;
  return `Medidas: A ${measurements.measureA} mm · B ${measurements.measureB} mm · DBL ${measurements.measureDbl} mm · ED ${measurements.measureEd} mm${measurements.baseCurve ? ` · Curva ${measurements.baseCurve}` : ""}`;
}

function pdfText(value: string): string {
  return value.replace(/·/g, "-").replace(/–/g, "-").replace(/—/g, "-");
}

async function buildLaboratoryPdf(sale: OpticalSale): Promise<Uint8Array> {
  const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");
  const document = await PDFDocument.create();
  const regular = await document.embedFont(StandardFonts.Helvetica);
  const bold = await document.embedFont(StandardFonts.HelveticaBold);
  const page = document.addPage([612, 792]);
  let y = 748;
  const write = (text: string, options: { bold?: boolean; size?: number; gap?: number } = {}) => {
    const font = options.bold ? bold : regular;
    const size = options.size || 11;
    const maxLength = options.size && options.size >= 18 ? 55 : 92;
    const words = pdfText(text).split(/\s+/);
    let line = "";
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if (candidate.length > maxLength && line) {
        page.drawText(line, { x: 48, y, size, font, color: rgb(0.03, 0.12, 0.2) });
        y -= size + 5;
        line = word;
      } else line = candidate;
    }
    if (line) page.drawText(line, { x: 48, y, size, font, color: rgb(0.03, 0.12, 0.2) });
    y -= options.gap || size + 8;
  };
  write("COSMO OPTICAS - ORDEN DE LABORATORIO", { bold: true, size: 18, gap: 30 });
  write(`${sale.folio} | Cotizacion ${sale.quoteFolio}`, { bold: true, size: 13 });
  write(`${sale.branchName} #${sale.branchCode} | Capturada ${shortDate(sale.createdAt)}`);
  write(`Paciente: ${sale.customer.fullName}`, { bold: true });
  write(`Entrega estimada: ${sale.estimatedDeliveryDate}`);
  y -= 8;
  write("ARMAZON", { bold: true, size: 13 });
  write(sale.frame.description);
  const frameMeasurements = frameMeasurementsLine(sale.frame);
  if (frameMeasurements) write(frameMeasurements);
  write(`Bisel: ${sale.bevelType}${sale.bevelFinish ? ` - ${sale.bevelFinish}` : ""}`);
  y -= 8;
  write("LENTES", { bold: true, size: 13 });
  write(sale.lens.description);
  y -= 8;
  write("GRADUACION", { bold: true, size: 13 });
  write(eyeLine("OD", sale.prescription.od));
  write(eyeLine("OI", sale.prescription.oi));
  if (sale.prescription.laboratoryComments) {
    y -= 8;
    write("COMENTARIOS PARA LABORATORIO", { bold: true, size: 13 });
    write(sale.prescription.laboratoryComments);
  }
  y -= 18;
  write("Estatus: PENDIENTE DE ENVIO A LABORATORIO", { bold: true });
  write(`Responsable de captura: ${sale.createdByName}`);
  return document.save();
}

async function buildCustomerFrameReceiptPdf(sale: OpticalSale): Promise<Uint8Array> {
  const details = sale.frame.customerDetails;
  if (sale.frame.origin !== "CUSTOMER" || !details) {
    throw new Error("Esta venta no contiene un armazón entregado por el cliente.");
  }
  const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");
  const document = await PDFDocument.create();
  const regular = await document.embedFont(StandardFonts.Helvetica);
  const bold = await document.embedFont(StandardFonts.HelveticaBold);
  const policy = "El cliente entrega su armazón y declara correctos los datos y observaciones. Cosmo Ópticas trabajará con cuidados razonables y estándares de calidad. En armazones ajenos o usados puede haber fractura, deformación, desprendimiento de recubrimientos o daños no visibles durante ajuste, calor, biselado o montaje. La recepción no implica asumir daños originados por desgaste, fatiga, reparaciones o deterioro preexistentes.";
  const copies = [
    "COPIA CLIENTE - ENTREGAR AL CLIENTE",
    "COPIA ÓPTICA - CONSERVAR EN SUCURSAL",
  ];
  for (const copyLabel of copies) {
    const page = document.addPage([226.77, 650]);
    let y = 626;
    const write = (text: string, options: { bold?: boolean; center?: boolean; gap?: number; size?: number } = {}) => {
      const font = options.bold ? bold : regular;
      const size = options.size || 8;
      const words = pdfText(text).split(/\s+/);
      const rows: string[] = [];
      let current = "";
      for (const word of words) {
        const candidate = current ? `${current} ${word}` : word;
        if (font.widthOfTextAtSize(candidate, size) > 198 && current) {
          rows.push(current);
          current = word;
        } else current = candidate;
      }
      if (current) rows.push(current);
      rows.forEach((row) => {
        const width = font.widthOfTextAtSize(row, size);
        page.drawText(row, {
          x: options.center ? Math.max(12, (226.77 - width) / 2) : 14,
          y,
          size,
          font,
          color: rgb(0.03, 0.12, 0.2),
        });
        y -= size + 4;
      });
      y -= options.gap || 2;
    };
    const rule = () => {
      page.drawLine({ start: { x: 14, y }, end: { x: 212.77, y }, thickness: 0.6, color: rgb(0.2, 0.45, 0.5) });
      y -= 13;
    };
    write("COSMO ÓPTICAS", { bold: true, center: true, size: 11 });
    write("MATERIAL DEL CLIENTE", { bold: true, center: true, size: 10 });
    write(sale.folio, { bold: true, center: true });
    write(shortDate(sale.createdAt), { center: true });
    write(`${sale.branchName} #${sale.branchCode}`, { center: true, gap: 5 });
    rule();
    write(copyLabel, { bold: true, center: true, gap: 6 });
    write(`CLIENTE: ${sale.customer.fullName}`, { bold: true });
    write(`MARCA: ${details.brand}`);
    write(`MODELO: ${details.model}`);
    write(`MATERIAL: ${details.material}`);
    write(`CONDICIÓN: ${details.condition}`);
    const frameMeasurements = frameMeasurementsLine(sale.frame);
    if (frameMeasurements) write(frameMeasurements);
    write(`BISEL: ${[sale.bevelType, sale.bevelFinish].filter(Boolean).join(" - ")}`);
    y -= 3;
    write("OBSERVACIONES", { bold: true });
    write(details.comments || "Sin observaciones visibles registradas.", { gap: 7 });
    rule();
    write(policy, { gap: 12 });
    page.drawLine({ start: { x: 18, y }, end: { x: 103, y }, thickness: 0.5, color: rgb(0.03, 0.12, 0.2) });
    page.drawLine({ start: { x: 123, y }, end: { x: 208, y }, thickness: 0.5, color: rgb(0.03, 0.12, 0.2) });
    y -= 13;
    page.drawText("Firma del cliente", { x: 26, y, size: 7, font: regular, color: rgb(0.03, 0.12, 0.2) });
    page.drawText("Nombre y firma de quien recibe", { x: 122, y, size: 6.5, font: regular, color: rgb(0.03, 0.12, 0.2) });
  }
  return document.save();
}

async function presentPdf(bytes: Uint8Array, filename: string, download: boolean): Promise<void> {
  if (download) downloadPdf(bytes, filename);
  else await printReceiptPdf(bytes, filename);
}

export function OpticalSaleConversion({ quote, session, onCancel, onSaved }: {
  quote: OpticalQuote;
  session: SessionIdentity;
  onCancel: () => void;
  onSaved: (sale: OpticalSale) => void;
}) {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMode>("01");
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [payments, setPayments] = useState<PaymentDraft[]>([]);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [operationKey] = useState(() => crypto.randomUUID());
  const [savedSale, setSavedSale] = useState<OpticalSale | null>(null);
  const [invoice, setInvoice] = useState<FiscalDraft>({ ...emptyFiscalDraft, email: quote.customer.email || "" });
  const [invoiceChoice, setInvoiceChoice] = useState<"" | "NO" | "SI">("");
  const [reviewing, setReviewing] = useState(false);
  const [submissionError, setSubmissionError] = useState("");

  const totals = useMemo(() => {
    const paid = roundMoney(payments.reduce((sum, payment) => sum + payment.amount, 0));
    const balance = roundMoney(Math.max(0, quote.total - paid));
    const percentage = quote.total > 0 ? roundMoney((paid / quote.total) * 100) : 0;
    return { paid, balance, percentage, minimum: roundMoney(quote.total * 0.1), laboratoryAuthorized: paid + 0.009 >= quote.total * 0.5 };
  }, [payments, quote.total]);

  function addPayment() {
    const amount = roundMoney(Number(paymentAmount));
    if (!amount || amount <= 0) { setMessage("Captura un importe mayor a cero."); return; }
    if (paymentMethod !== "01" && !paymentReference.trim()) {
      setMessage(paymentMethod === "INTERNAL_CREDIT_NOTE"
        ? "Captura el código único de la nota de crédito."
        : "La referencia es obligatoria cuando el pago no es en efectivo."); return;
    }
    if (paymentMethod === "INTERNAL_CREDIT_NOTE" && !/^[A-Z2-9]{10}$/.test(paymentReference.trim().toUpperCase())) {
      setMessage("El código único de la nota de crédito debe contener 10 caracteres."); return;
    }
    if (amount > totals.balance + 0.009) { setMessage("El pago no puede superar el saldo pendiente."); return; }
    setPayments([...payments, paymentMethod === "INTERNAL_CREDIT_NOTE"
      ? { kind: "INTERNAL_CREDIT_NOTE", redemptionCode: paymentReference.trim().toUpperCase(), amount }
      : { kind: "STANDARD", methodCode: paymentMethod, reference: paymentReference.trim(), amount }]);
    setPaymentReference(""); setPaymentAmount(""); setMessage("");
    setReviewing(false);
  }

  function reviewSale() {
    if (!payments.length) { setMessage("Agrega al menos una forma de pago."); return; }
    if (totals.paid + 0.009 < totals.minimum) { setMessage(`El pago inicial mínimo es ${money.format(totals.minimum)} (10% del total).`); return; }
    if (totals.balance <= 0.009 && !invoiceChoice) { setMessage("Confirma si el cliente requiere factura para liquidar la venta."); return; }
    if (totals.balance <= 0.009 && invoiceChoice === "SI"
      && (!invoice.rfc.trim() || !invoice.legalName.trim() || !invoice.fiscalPostalCode.trim() || !invoice.email.trim())) {
      setMessage("Completa los datos fiscales obligatorios antes de revisar la venta."); return;
    }
    setMessage(""); setSubmissionError(""); setReviewing(true);
  }

  async function saveSale() {
    if (!payments.length) { setMessage("Agrega al menos una forma de pago."); return; }
    if (totals.paid + 0.009 < totals.minimum) { setMessage(`El pago inicial mínimo es ${money.format(totals.minimum)} (10% del total).`); return; }
    if (totals.balance <= 0.009 && !invoiceChoice) { setMessage("Confirma si el cliente requiere factura para liquidar la venta."); return; }
    setSaving(true); setMessage(""); setSubmissionError("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/sales/optical`, {
        method: "POST", credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json", "Idempotency-Key": operationKey },
        body: JSON.stringify({
          quoteId: quote.id,
          payments: payments.map((payment) => payment.kind === "STANDARD"
            ? { ...payment, reference: payment.reference || null } : payment),
          invoiceDecision: totals.balance <= 0.009
            ? invoiceChoice === "SI" ? "YES" : "NO"
            : "NOT_APPLICABLE",
          invoice: totals.balance <= 0.009 && invoiceChoice === "SI"
            ? invoicePayload({ ...invoice, requested: true }) : { requested: false },
        }),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const sale = opticalSaleResponseSchema.parse(await response.json()).data;
      setSavedSale(sale);
      onSaved(sale);
      const notices: string[] = [`${sale.folio} registrada correctamente.`];
      if (payments.some((payment) => payment.kind === "STANDARD" && payment.methodCode === "01")) {
        const drawer = await openAuditedCashDrawer({
          openingType: "OPTICAL_SALE_CASH",
          operationReference: sale.folio,
          paymentSequence: null,
          reason: "Cobro en efectivo de venta graduada",
        });
        notices.push(drawer.message);
      }
      try {
        const printResult = await printReceiptPdf(await buildOpticalTicketPackagePdf(sale), opticalDocumentFilename(sale.folio, "FULL_PACKAGE"));
        notices.push(printResult === "LOCAL"
          ? "Se imprimió el paquete completo de documentos."
          : "No se encontró impresión local; el paquete se abrió en el navegador.");
      } catch {
        notices.push("La venta quedó guardada, pero el paquete de tickets no pudo imprimirse automáticamente.");
      }
      setMessage(notices.join(" "));
    } catch (cause) {
      const errorMessage = cause instanceof Error ? cause.message : "No fue posible convertir la cotización.";
      setMessage(errorMessage);
      setSubmissionError(errorMessage);
    } finally { setSaving(false); }
  }

  const branch = session.branches.find((item) => item.id === session.branchId);
  if (savedSale) {
    return <section className="quote-success optical-sale-success" aria-live="polite">
      <span>Venta graduada registrada</span><strong>{savedSale.folio}</strong>
      <p>{savedSale.status === "LAYAWAY"
        ? `Apartado recibido por ${money.format(savedSale.paid)}. La orden de laboratorio permanece bloqueada hasta completar 50%.`
        : savedSale.status === "PAID"
          ? "Venta liquidada y orden de laboratorio autorizada."
          : `Pago registrado por ${money.format(savedSale.paid)}. Orden de laboratorio autorizada.`}</p>
      <div className="optical-sale-actions">
        <button className="primary-button" onClick={async () => presentPdf(await buildOpticalTicketPackagePdf(savedSale), opticalDocumentFilename(savedSale.folio, "FULL_PACKAGE"), false)}>Imprimir paquete completo</button>
        <button className="secondary-button" onClick={async () => presentPdf(await buildOpticalTicketPackagePdf(savedSale), opticalDocumentFilename(savedSale.folio, "FULL_PACKAGE"), true)}>Descargar paquete completo</button>
        <button className="secondary-button" onClick={async () => presentPdf(await buildOpticalCustomerTicketPdf(savedSale), opticalDocumentFilename(savedSale.folio, "CUSTOMER_TICKET"), false)}>Reimprimir comprobante del cliente</button>
        <button className="secondary-button" onClick={async () => presentPdf(await buildOpticalInternalTicketPdf(savedSale), opticalDocumentFilename(savedSale.folio, "OPTICAL_COPY"), false)}>Reimprimir copia de la óptica</button>
        {savedSale.frame.origin === "CUSTOMER" && savedSale.frame.customerDetails && <>
          <button className="secondary-button" onClick={async () => presentPdf(await buildCustomerFrameResponsivaPdf(savedSale), opticalDocumentFilename(savedSale.folio, "CUSTOMER_FRAME_RESPONSIVA"), true)}>Descargar responsiva · 2 copias</button>
          <button className="secondary-button" onClick={async () => presentPdf(await buildCustomerFrameResponsivaPdf(savedSale), opticalDocumentFilename(savedSale.folio, "CUSTOMER_FRAME_RESPONSIVA"), false)}>Imprimir responsiva · 2 copias</button>
        </>}
        {savedSale.laboratoryAuthorized && <>
          <button className="secondary-button" onClick={async () => presentPdf(await buildOpticalLaboratoryTicketPdf(savedSale), opticalDocumentFilename(savedSale.folio, "LABORATORY_ORDER"), false)}>Reimprimir orden de laboratorio</button>
          <button className="secondary-button" onClick={async () => presentPdf(await buildOpticalLaboratoryTicketPdf(savedSale), opticalDocumentFilename(savedSale.folio, "LABORATORY_ORDER"), true)}>Descargar orden de laboratorio</button>
        </>}
        <button className="text-button" onClick={onCancel}>Cerrar</button>
      </div>
    </section>;
  }

  return <section className="sales-card optical-conversion" aria-labelledby="optical-conversion-title">
    <div className="section-heading"><div><p className="eyebrow">Conversión protegida</p><h2 id="optical-conversion-title">Convertir {quote.folio} en venta graduada</h2><p>Cliente, receta, armazón, lentes y precio se conservan exactamente como fueron cotizados.</p></div><button className="secondary-button" disabled={saving} onClick={onCancel}>Cancelar</button></div>
    {message && <p className="form-message info-message" aria-live="polite">{message}</p>}
    <div className="optical-locked-summary">
      <div><span>Cliente</span><strong>{quote.customer.fullName}</strong></div>
      <div><span>Armazón</span><strong>{quote.frame.description}</strong>{quote.frame.measurements && <small>{frameMeasurementsLine(quote.frame)}</small>}</div>
      <div><span>Lentes</span><strong>{quote.lens.description}</strong></div>
      <div><span>Entrega estimada</span><strong>{quote.estimatedDeliveryDate}</strong></div>
      <div><span>Total cotizado</span><strong>{money.format(quote.total)}</strong></div>
    </div>
    <section className="lens-builder"><div><h3>Pago inicial</h3><p>El mínimo es 10%. La orden de laboratorio se autoriza al cubrir 50% del total.</p></div><div className="counter-payment-entry"><label>Forma de pago<select disabled={saving || reviewing} onChange={(event) => setPaymentMethod(event.target.value as PaymentMode)} value={paymentMethod}>{paymentMethods.map((method) => <option key={method.code} value={method.code}>{method.code} - {method.label}</option>)}<option value="INTERNAL_CREDIT_NOTE">99 - Nota de crédito interna</option></select></label><label>{paymentMethod === "INTERNAL_CREDIT_NOTE" ? "Código único de la nota" : "Referencia"}<input autoCapitalize="characters" disabled={saving || reviewing || paymentMethod === "01"} maxLength={paymentMethod === "INTERNAL_CREDIT_NOTE" ? 10 : 120} onChange={(event) => setPaymentReference(paymentMethod === "INTERNAL_CREDIT_NOTE" ? event.target.value.toUpperCase().replace(/[^A-Z2-9]/g, "") : event.target.value)} placeholder={paymentMethod === "01" ? "No aplica" : paymentMethod === "INTERNAL_CREDIT_NOTE" ? "10 caracteres" : "Obligatoria"} value={paymentReference} /></label><label>Importe<input disabled={saving || reviewing || totals.balance <= 0} min="0.01" onChange={(event) => setPaymentAmount(event.target.value)} placeholder={totals.balance.toFixed(2)} step="0.01" type="number" value={paymentAmount} /></label><button className="primary-button" disabled={saving || reviewing || totals.balance <= 0} onClick={addPayment} type="button">Agregar pago</button></div><div className="counter-payments">{payments.map((payment, index) => <div key={`${payment.kind}-${index}`}><span>{payment.kind === "INTERNAL_CREDIT_NOTE" ? "99 - Nota de crédito interna" : `${payment.methodCode} - ${paymentMethods.find((method) => method.code === payment.methodCode)?.label}`}</span><span>{payment.kind === "INTERNAL_CREDIT_NOTE" ? `Código protegido terminado en ${payment.redemptionCode.slice(-4)}` : payment.reference || "Sin referencia"}</span><strong>{money.format(payment.amount)}</strong><button className="text-button" disabled={saving || reviewing} onClick={() => { setPayments(payments.filter((_, paymentIndex) => paymentIndex !== index)); setReviewing(false); }}>Quitar</button></div>)}{payments.length === 0 && <p className="empty-state">Aún no hay pagos agregados.</p>}</div></section>
    <div className="counter-totals"><div><span>Total cotizado</span><strong>{money.format(quote.total)}</strong></div><div><span>Pago inicial mínimo (10%)</span><strong>{money.format(totals.minimum)}</strong></div><div><span>Total recibido</span><strong>{money.format(totals.paid)}</strong></div><div><span>Porcentaje cubierto</span><strong>{totals.percentage.toFixed(2)}%</strong></div><div><span>Saldo pendiente</span><strong>{money.format(totals.balance)}</strong></div><div className={totals.laboratoryAuthorized ? "optical-lab-authorized" : "optical-lab-blocked"}><span>Orden de laboratorio</span><strong>{totals.laboratoryAuthorized ? "Autorizada al guardar" : "Pendiente hasta cubrir 50%"}</strong></div></div>
    {totals.balance <= 0.009 ? <section className="sales-card fiscal-request-card"><div className="operation-panel-heading"><div><h2>Confirmación fiscal al liquidar</h2><p>La decisión se registra únicamente porque el saldo quedará en cero.</p></div><label className="fiscal-toggle">¿El cliente requiere factura?<select disabled={saving || reviewing} onChange={(event) => { const choice = event.target.value as "" | "NO" | "SI"; setInvoiceChoice(choice); setInvoice({ ...invoice, requested: choice === "SI" }); setReviewing(false); }} value={invoiceChoice}><option value="">Selecciona</option><option value="NO">No</option><option value="SI">Sí</option></select></label></div></section> : <p className="lens-notice">Facturación disponible cuando la venta quede liquidada. En este momento se registrará como venta abierta.</p>}
    {totals.balance <= 0.009 && invoiceChoice === "SI" && <FiscalRequestFields disabled={saving || reviewing} hideDecision onChange={setInvoice} value={{ ...invoice, requested: true }} />}
    {reviewing && <section className="operation-panel"><div className="operation-panel-heading"><div><h2>Revisión final de la venta</h2><p>Confirma los importes y la decisión fiscal antes de registrar el movimiento.</p></div></div><div className="optical-locked-summary"><div><span>Cliente</span><strong>{quote.customer.fullName}</strong></div><div><span>Total</span><strong>{money.format(quote.total)}</strong></div><div><span>Recibido</span><strong>{money.format(totals.paid)}</strong></div><div><span>Saldo</span><strong>{money.format(totals.balance)}</strong></div><div><span>Factura</span><strong>{totals.balance > 0.009 ? "Disponible al liquidar" : invoiceChoice === "SI" ? "Sí, con datos fiscales" : "No solicitada"}</strong></div></div><p className="warning-message">Al confirmar se asignará el folio de venta, se aplicarán los pagos y cualquier nota de crédito quedará consumida de forma auditada.</p></section>}
    {submissionError && <p className="form-message error-message" aria-live="assertive"><strong>No se registró la venta.</strong> {submissionError}</p>}
    <div className="step-actions"><span className="branch-chip">Sucursal: {branch?.code} · {branch?.name}</span>{reviewing ? <><button className="secondary-button" disabled={saving} onClick={() => { setReviewing(false); setSubmissionError(""); }}>Corregir</button><button className="primary-button" disabled={saving} onClick={saveSale}>{saving ? "Registrando venta…" : "Confirmar venta y generar ticket"}</button></> : <button className="primary-button" disabled={saving || totals.paid + 0.009 < totals.minimum} onClick={reviewSale}>Revisar venta</button>}</div>
  </section>;
}
